import { useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface LaborLogoProps {
  /** Labor name — used for the alt text and the `name` fallback. */
  readonly name: string;
  /** Same-origin logo path; `null`/`undefined`/empty falls back. */
  readonly logoUrl: string | null | undefined;
  /** What to show without a (loadable) logo. */
  readonly fallback?: 'placeholder' | 'name';
}

/**
 * Shows a Labor logo (120×40, contained). Without a URL or when loading fails,
 * renders a neutral placeholder, optionally with the Labor name.
 */
export function LaborLogo({ name, logoUrl, fallback = 'placeholder' }: LaborLogoProps) {
  const { t } = useTranslation('app');
  const [failed, setFailed] = useState(false);

  // Retry loading when the URL changes (e.g. after a re-upload).
  useEffect(() => {
    setFailed(false);
  }, [logoUrl]);

  if (logoUrl != null && logoUrl !== '' && !failed) {
    return (
      <div className="flex h-10 w-[120px] shrink-0 items-center justify-center overflow-hidden">
        <img
          src={logoUrl}
          alt={t('portal.laborLogo.alt', { name })}
          className="h-full w-full object-contain"
          onError={() => setFailed(true)}
        />
      </div>
    );
  }

  const placeholder = (
    <div
      data-testid="labor-logo-placeholder"
      className="flex h-10 w-10 shrink-0 items-center justify-center bg-muted"
      aria-hidden="true"
    >
      <Building2 className="h-5 w-5 text-muted-foreground" />
    </div>
  );

  if (fallback === 'name') {
    return (
      <div className="flex items-center gap-2">
        {placeholder}
        <span className="text-sm font-medium text-foreground">{name}</span>
      </div>
    );
  }

  return placeholder;
}
